<template>
  <div v-if="insertion">
    <div
      v-if="isPreparingModal"
      :class="$style.preparingOverlay"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <HgSpinner size="large" />
      <span :class="$style.preparingOverlayLabel">{{ t('toplisting.modal.preparing') }}</span>
    </div>
    <HgModal
      v-model="internalModel"
      name="toplistingModal"
      :containerCssClass="$style.modalContainer"
      :class="$style.modalContainerWrapper"
      :fullScreenMobile="true"
      :tabindex="-1"
      @close="onModalClose"
    >
      <template #title>{{ bookingFormTitle }}</template>
      <ToplistingBookingForm
        ref="bookingForm"
        :insertion="insertion"
        :conversionSource="props.conversionSource"
        :active="internalModel"
        @update:title="bookingFormTitle = $event"
        @update:footerBindings="footerBindings = $event"
        @close-request="onBookingFormCloseRequest"
        @deactivate-request="openDeactivateDialog"
      />
      <template #footerAction>
        <HgLink
          v-if="
            footerBindings.toplistingModalAction === ToplistingModalAction.EDIT &&
            !footerBindings.isModalReadOnly
          "
          ref="deactivateToplisting"
          :class="$style.wrapperIcon"
          :disabled="footerBindings.isDeactivateButtonDisabled"
          @click="openDeactivateDialog"
        >
          <HgIcon icon="Delete" size="large" theme="secondary" />
          {{ deactivationLinkText }}
        </HgLink>
      </template>
      <template #footer>
        <HgButton
          type="button"
          theme="primary-bordered"
          :size="ButtonSize.Small"
          @click="onFooterCancel"
        >
          {{
            footerBindings.isModalReadOnly
              ? t('general.buttons.close')
              : t('general.buttons.cancel')
          }}
        </HgButton>
        <HgButton
          v-if="!footerBindings.isSubmitButtonHidden && !footerBindings.isModalReadOnly"
          ref="bookButton"
          :disabled="footerBindings.isPrimaryActionDisabled"
          theme="primary"
          :size="ButtonSize.Small"
          @click="onFooterSubmit"
        >
          <LoadingIndicator
            v-if="footerBindings.submitLoadingIndicator"
            :class="$style.loadingIndicator"
          />
          {{ footerBindings.submitButtonText }}
        </HgButton>
      </template>
    </HgModal>
    <ConfirmationDialog
      ref="deactivationDialog"
      v-model="showDeactivateDialog"
      :buttonStyle="$style.deactivationDialogButtons"
      :confirmText="toplistingDeactivationConfirmText"
      :getterForAsyncCallInProgress="isProjectBoostDeactivationInProgress"
      :title="toplistingDeactivationTitle"
      :trackingCancelAction="EventAction.CANCEL_DEACTIVATE_TOPLISTING"
      :trackingCloseAction="EventAction.CANCEL_DEACTIVATE_TOPLISTING"
      :trackingConfirmAction="EventAction.DEACTIVATE_TOPLISTING"
      :trackingListingId="insertion.listing?.id ?? undefined"
      @confirm="confirmToplistingDeactivation"
      @closeDialog="showDeactivateDialog = false"
    >
      {{ toplistingDeactivationBody }}
    </ConfirmationDialog>
  </div>
</template>

<script setup lang="ts">
import { OfferType, RankType } from '@smg-real-estate/hg-rets-schema';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import type { ComponentExposed } from 'vue-component-type-helpers';
import { useI18n } from 'vue-i18n';

import LoadingIndicator from '@/assets/images/loading_indicator.svg';
import ConfirmationDialog from '@/components/ConfirmationDialog/ConfirmationDialog.vue';
import ToplistingBookingForm, {
  type FooterBindings,
} from '@/components/ToplistingBookingForm/ToplistingBookingForm.vue';
import handleErrorWithNotification from '@/helpers/handleErrorWithNotification';
import { getUbmPriceElasticityExperimentVariantArray } from '@/helpers/toplistingPriceElasticityTracking';
import { ConversionSource, EventAction, EventLabel } from '@/services/tracking/GtmTrackingService';
import {
  trackToplistingDeactivationModalDisplayed,
  trackToplistingInteraction,
  trackToplistingModalDisplayed,
} from '@/services/tracking/Tracking';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import { useToplistingStore } from '@/stores/toplisting/useToplistingStore';
import { ApiCallStatus } from '@/stores/types/insertions';
import { ToplistingModalAction, type ToplistingModalInsertionContext } from '@/types';
import { ButtonSize, HgButton, HgIcon, HgLink, HgModal, HgSpinner } from '@smg-real-estate/domus';

const defaultFooterBindings = (): FooterBindings => ({
  isPrimaryActionDisabled: true,
  submitLoadingIndicator: false,
  submitButtonText: '',
  isSubmitButtonHidden: false,
  isModalReadOnly: false,
  toplistingModalAction: ToplistingModalAction.CREATE,
  isDeactivateButtonDisabled: false,
  isNoPromotionSelected: false,
  isProjectBoostApiCallInProgress: false,
});

interface Props {
  modelValue: boolean;
  insertion?: ToplistingModalInsertionContext;
  conversionSource?: ConversionSource;
}

const props = defineProps<Props>();

const emit = defineEmits(['update:modelValue', 'closeDialog']);

const toplistingStore = useToplistingStore();
const agencyStore = useAgencyStore();
const { activeAgencyId } = storeToRefs(agencyStore);
const { t } = useI18n();

const bookingFormComponentRef =
  useTemplateRef<ComponentExposed<typeof ToplistingBookingForm>>('bookingForm');

const bookingFormTitle = ref<string>('');
const showDeactivateDialog = ref<boolean>(false);
const footerBindings = ref<FooterBindings>(defaultFooterBindings());

const toplistingExperimentVariantArray = computed<string | undefined>(() =>
  getUbmPriceElasticityExperimentVariantArray({
    customerId: activeAgencyId.value,
    objectReference: props.insertion?.objectReference,
  }),
);

const toplistingDeactivationProductName = computed<string>(() =>
  props.insertion?.toplistingData?.listingType === RankType.Premium
    ? t('toplisting.badge.premium')
    : t('toplisting.badge.top'),
);

/** NC Boost deactivation runs while this dialog stays open, so guard against a second confirm. */
const isProjectBoostDeactivationInProgress = (): boolean =>
  footerBindings.value.isProjectBoostApiCallInProgress;

/** Products stored outside the toplisting service share the same `deactivate.*` key shape. */
const deactivationCopyNamespace = computed<string | undefined>(() => {
  if (footerBindings.value.deactivationKind === 'regionalHighlight') {
    return 'toplisting.modal.regionalHighlight.deactivate';
  }
  if (footerBindings.value.deactivationKind === 'projectBoost') {
    return 'toplisting.modal.projectBoost.deactivate';
  }
  return undefined;
});

const toplistingDeactivationTitle = computed<string>(() =>
  deactivationCopyNamespace.value
    ? t(`${deactivationCopyNamespace.value}.title`)
    : t('toplisting.modal.deactivationExperiment.title', {
        productName: toplistingDeactivationProductName.value,
      }),
);

const toplistingDeactivationBody = computed<string>(() =>
  deactivationCopyNamespace.value
    ? t(`${deactivationCopyNamespace.value}.body`)
    : t('toplisting.modal.deactivationExperiment.body', {
        productName: toplistingDeactivationProductName.value,
      }),
);

const toplistingDeactivationConfirmText = computed<string>(() =>
  deactivationCopyNamespace.value
    ? t(`${deactivationCopyNamespace.value}.confirm`)
    : t('toplisting.modal.deactivationExperiment.confirmText'),
);
const deactivationLinkText = computed<string>(() =>
  deactivationCopyNamespace.value
    ? t(`${deactivationCopyNamespace.value}.link`)
    : t('toplisting.modal.deactivate'),
);

watch(
  () => props.modelValue,
  (isModalOpen) => {
    if (isModalOpen) {
      if (!props.conversionSource) {
        throw new Error('ToplistingModal: conversionSource is required');
      }
      const listingId = props.insertion?.listing?.id ?? undefined;
      trackToplistingModalDisplayed(
        listingId,
        props.conversionSource,
        !listingId ? props.insertion?.objectReference || undefined : undefined,
        toplistingExperimentVariantArray.value,
      );
    }
  },
  { immediate: true },
);

const isPreparingModal = ref(false);

/** Fetch listing-aware slots before opening so backend eligibility determines the RH card. */
const isRegionalHighlightEligible = computed<boolean>(() => {
  const listingId = props.insertion?.listing?.id;
  if (!listingId) {
    return false;
  }
  if (
    props.insertion?.listing?.offerType === OfferType.Buy ||
    props.insertion?.listing?.offerType === OfferType.Rent
  ) {
    return true;
  }
  const statuses = toplistingStore.regionalHighlightListingStatuses as
    | Record<string, { hasRegionalHighlight?: boolean }>
    | undefined;
  return statuses?.[listingId]?.hasRegionalHighlight === true;
});

/**
 * True once the store already holds a successful slots response for this exact listing (e.g.
 * from a previous open, or warmed by `ToplistingBookingForm` itself). Avoids re-fetching — and
 * re-holding the modal closed — on every open once the cache is warm.
 */
const hasFreshRegionalHighlightDataForListing = computed<boolean>(() => {
  const listingId = props.insertion?.listing?.id;
  if (!listingId) {
    return false;
  }
  const apiStatus = toplistingStore.regionalHighlightApiStatus as { slots?: string } | undefined;
  const slotsResponse = toplistingStore.regionalHighlightSlotsResponse as
    | { listing?: { listingId?: string } }
    | undefined;
  return (
    apiStatus?.slots === ApiCallStatus.SUCCESS && slotsResponse?.listing?.listingId === listingId
  );
});

/** Prevents the background page from scrolling behind the full-screen preparing overlay. */
const setBackgroundScrollLocked = (locked: boolean): void => {
  document.body.style.overflow = locked ? 'hidden' : '';
};

watch(
  () => props.modelValue,
  async (isModalOpen) => {
    if (
      !isModalOpen ||
      !isRegionalHighlightEligible.value ||
      hasFreshRegionalHighlightDataForListing.value
    ) {
      return;
    }
    isPreparingModal.value = true;
    setBackgroundScrollLocked(true);
    try {
      await toplistingStore.fetchRegionalHighlightSlots(props.insertion?.listing?.id ?? undefined);
    } finally {
      isPreparingModal.value = false;
      setBackgroundScrollLocked(false);
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  setBackgroundScrollLocked(false);
});

// in case you ever read comments or even code, `internalModel` is the UBM way to toggle modals opened/closed
const internalModel = computed<boolean>({
  get: () => props.modelValue && !isPreparingModal.value,
  set: (val) => {
    emit('update:modelValue', val);
    if (!val) {
      emit('closeDialog');
    }
  },
});

const onModalClose = (): void => {
  internalModel.value = false;
  const experimentVariantArray = toplistingExperimentVariantArray;
  if (!props.conversionSource) {
    throw new Error('onModalClose: conversionSource is required');
  }

  trackToplistingInteraction({
    action: EventAction.CLOSE_TOPLISTING,
    label: EventLabel.IN_MODAL,
    listingId: props.insertion?.listing?.id ?? undefined,
    objectReferenceId: !props.insertion?.listing?.id
      ? props.insertion?.objectReference || undefined
      : undefined,
    conversionSource: props.conversionSource,
    ...(experimentVariantArray.value !== undefined
      ? { experimentVariantArray: experimentVariantArray.value }
      : {}),
  });
};

const onFooterCancel = (): void => {
  bookingFormComponentRef.value?.cancel();
};

const onFooterSubmit = (): void => {
  bookingFormComponentRef.value?.submitForm();
};

const onBookingFormCloseRequest = (): void => {
  internalModel.value = false;
};
const openDeactivateDialog = (): void => {
  if (!showDeactivateDialog.value) {
    trackToplistingDeactivationModalDisplayed(props.insertion?.listing?.id ?? undefined);
  }
  showDeactivateDialog.value = true;
};

const confirmToplistingDeactivation = async (): Promise<void> => {
  if (footerBindings.value.deactivationKind === 'regionalHighlight') {
    await handleErrorWithNotification(async () => {
      await bookingFormComponentRef.value?.deactivateRegionalHighlight();
      internalModel.value = false;
      showDeactivateDialog.value = false;
    });
    return;
  }

  if (footerBindings.value.deactivationKind === 'projectBoost') {
    await handleErrorWithNotification(async () => {
      await bookingFormComponentRef.value?.deactivateProjectBoost();
      internalModel.value = false;
      showDeactivateDialog.value = false;
    });
    return;
  }

  internalModel.value = false;
  showDeactivateDialog.value = false;

  const insertion = props.insertion;
  if (!insertion) {
    return;
  }

  handleErrorWithNotification(
    () =>
      toplistingStore.deleteToplisting({
        insertionId: insertion.id,
        objectReferenceId: insertion.objectReference,
      }),
    { insertionId: props.insertion?.id },
  );
};

defineExpose({
  showDeactivateDialog,
  openDeactivateDialog,
});
</script>

<style lang="scss" module>
@import '@/assets/variables';
@import '@/assets/mixins';

.wrapperIcon {
  @include icon-link-wrapper;
}

.preparingOverlay {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgb(0 0 0 / 0.3);
}

.preparingOverlayLabel {
  @include body-3-bold($color-white);

  & {
    margin-top: $space-100;
  }
}

.loadingIndicator {
  width: var(--typo-body-3-font-size);
  padding-right: $space-100;
  filter: grayscale(1) brightness(300%);
}

.deactivationDialogButtons {
  margin: $space-50;

  &:nth-child(2) {
    margin-right: 20px;
    color: #fff;
    background-color: $color-danger;
    border-color: $color-danger;

    &:hover,
    &:focus {
      background-color: $color-danger-hover;
      border-color: $color-danger-hover;
    }
  }
}

@media screen and (min-width: $viewport-sm-min) {
  .modalContainerWrapper .modalContainer {
    max-width: 750px;
  }
}
</style>
